import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Box, Heading, Text, VStack, HStack, SimpleGrid, Badge, Icon, Flex, Button,
  Grid, GridItem, IconButton, useToast, useMediaQuery, Progress,
} from "@chakra-ui/react";
import {
  FiCalendar, FiFileText, FiMapPin, FiAlertCircle, FiArrowRight, FiChevronLeft,
  FiChevronRight, FiClock, FiBook, FiDollarSign, FiSun,
  FiVideo, FiPlayCircle, FiBell, FiCreditCard, FiMessageCircle, FiHome, FiCopy,
} from "react-icons/fi";
import { FaCalendarPlus } from "react-icons/fa";
import moment from "moment";
import {
  formatTime12h, formatCurrency, formatInvoiceDate, getCertStatus, getModuleStatus,
  getDueInBadge, isSupersededInvoice,
} from "../utils/formatHelpers";
import { generateICSContent, downloadICS } from "../utils/calendarHelpers";
import { getJoinButtonState, openMeetingLink, copyMeetingLink } from "../utils/meetingHelpers";
import { COLORS } from "../styles/colors";
import { fetchDashboardTopNotices, sanitizeNoticeHtml, getNoticeCardStyle } from "../utils/lmsNotices";
import { NoticeAttachments } from "./NoticeAttachments";
import {
  EVALUATION_DUE_LABEL,
  fetchPublishedEvaluationForm,
  fetchSubmittedCourseRunIds,
  getEvaluationDueBadgeColors,
  isEvaluationOpenForCourseRun,
} from "../utils/lmsEvaluations";

/** Same rich-text styles as NoticesTab (bold / italic / links / lists). */
const noticeBodySx = {
  fontSize: { base: "xs", md: "sm" },
  color: "gray.600",
  lineHeight: "tall",
  "& p": { mb: 2 },
  "& p:last-child": { mb: 0 },
  "& ul": { pl: 5, my: 1, listStyleType: "disc" },
  "& ol": { pl: 5, my: 1, listStyleType: "decimal" },
  "& strong, & b": { fontWeight: 600 },
  "& em, & i": { fontStyle: "italic" },
  "& a": { color: "blue.600", textDecoration: "underline" },
};

const INVOICES_TAB = 3;
const DOCUMENTS_TAB = 2;
const DUE_SOON_DAYS = 14;
const MAX_OVERDUE_ROWS = 3;
const MAX_DUE_SOON_ROWS = 2;

const isUnpaidInvoice = (inv) => {
  const status = String(inv?.status ?? "").toLowerCase();
  return status !== "paid" && status !== "superseded" && status !== "waived" && status !== "inactive";
};

const getInvoiceTotal = (inv) => {
  const subtotal = inv.invoice_line_items?.length > 0
    ? inv.invoice_line_items.reduce((sum, item) => sum + (Number(item.unit_price || 0) * Number(item.quantity || 1)), 0)
    : 0;
  const fromLines = subtotal + Number(inv.gst_amount || 0);
  if (fromLines > 0) return fromLines;
  if (inv.net_amount != null && Number(inv.net_amount) > 0) return Number(inv.net_amount);
  if (inv.unit_price && Number(inv.unit_price) > 0) return Number(inv.unit_price);
  if (inv.total_amount && Number(inv.total_amount) > 0) return Number(inv.total_amount);
  return 0;
};

const getInvoiceRemaining = (inv) => Math.max(0, getInvoiceTotal(inv) - Number(inv.amount_paid || 0));

const getInvoiceTitle = (inv) => {
  const descriptions = (inv.invoice_line_items || [])
    .map((item) => item.description)
    .filter(Boolean);
  if (descriptions.length > 0) return descriptions.join(", ");
  if (inv.invoice_type) {
    return inv.invoice_type
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }
  return "Invoice";
};

/** Pull AM / PM / SAT from run id, run_name, or code when present. */
const getRunSlotLabel = (...sources) => {
  const text = sources.filter(Boolean).join(" ");
  const match = String(text).match(/\b(AM|PM|SAT)\b/i);
  return match ? match[1].toUpperCase() : null;
};

export const DashboardTab = ({
  certifications,
  modules,
  invoices,
  documents,
  greetingName = "there",
  onNavigateTab,
  registrationMap = {},
}) => {
  const allModules = Object.values(modules).flat();
  const allInvoices = Object.values(invoices).flat().filter((inv) => !isSupersededInvoice(inv));
  const allDocuments = Object.values(documents).flat();
  const toast = useToast();
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const [topNotices, setTopNotices] = useState([]);
  const [evaluationDueRunIds, setEvaluationDueRunIds] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    const loadEvaluationDue = async () => {
      const form = await fetchPublishedEvaluationForm();
      if (cancelled) return;
      if (!form?.id) {
        setEvaluationDueRunIds(new Set());
        return;
      }
      const registrationIds = Object.values(registrationMap || {}).flat().filter(Boolean);
      if (registrationIds.length === 0) {
        setEvaluationDueRunIds(new Set());
        return;
      }
      const submitted = await fetchSubmittedCourseRunIds({
        formId: form.id,
        registrationIds,
      });
      if (cancelled) return;
      const due = new Set();
      Object.values(modules || {}).forEach((list) => {
        (list || []).forEach((mod) => {
          if (!mod?.id) return;
          if (!isEvaluationOpenForCourseRun(form, mod)) return;
          if (!submitted.has(mod.id)) due.add(mod.id);
        });
      });
      setEvaluationDueRunIds(due);
    };
    loadEvaluationDue();
    return () => {
      cancelled = true;
    };
  }, [modules, registrationMap]);

  useEffect(() => {
    let cancelled = false;
    const courseScopes = allModules.map((mod) => ({
      courseTypeCode: mod.course_type_code || mod.course_type?.code || null,
      courseRunId: mod.id || null,
    }));
    fetchDashboardTopNotices(certifications, courseScopes)
      .then((rows) => {
        if (!cancelled) setTopNotices(rows || []);
      })
      .catch((err) => {
        console.error("Error loading dashboard notices:", err);
        if (!cancelled) setTopNotices([]);
      });
    return () => { cancelled = true; };
    // allModules is derived from modules; use modules + certifications as deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certifications, modules]);

  const totalCourses = allModules.length;
  const pendingDocs = allDocuments.filter((d) => d.status !== "done").length;
  const unpaidInvoiceList = allInvoices.filter(isUnpaidInvoice);
  const unpaidInvoices = unpaidInvoiceList.length;

  const today = moment().startOf("day");
  const dueSoonEnd = moment().add(DUE_SOON_DAYS, "days").endOf("day");
  const weekFromNow = moment().add(7, "days").endOf("day");
  const monthStart = moment().startOf("month");
  const monthEnd = moment().endOf("month");

  const allSessions = [];
  Object.entries(modules).forEach(([certId, certModules]) => {
    const cert = certifications.find((c) => c.id === certId);
    certModules.forEach((mod) => {
      (mod.course_dates || []).forEach((cd) => {
        allSessions.push({
          date: cd.date,
          start_time: cd.start_time,
          end_time: cd.end_time,
          venue: cd.venue,
          venueAddress: cd.venueAddress,
          room: cd.room,
          meeting_url: cd.meeting_url || "",
          is_online: !!cd.is_online || !!cd.meeting_url,
          moduleName: mod.course_type?.name || mod.course_type_code,
          moduleId: mod.id,
          certName: cert?.full_certification?.name || "",
        });
      });
    });
  });
  allSessions.sort((a, b) => moment(a.date).diff(moment(b.date)));

  const upcomingSessions = allSessions.filter((s) => {
    const d = moment(s.date);
    return d.isBetween(today, weekFromNow, null, "[]");
  });

  const monthSessions = allSessions.filter((s) => {
    const d = moment(s.date);
    return d.isBetween(monthStart, monthEnd, "day", "[]") && !d.isBefore(today);
  });

  const sessionsByDate = {};
  allSessions.forEach((s) => {
    const key = moment(s.date).format("YYYY-MM-DD");
    if (!sessionsByDate[key]) sessionsByDate[key] = [];
    sessionsByDate[key].push(s);
  });

  const overdueInvoices = unpaidInvoiceList
    .filter((inv) => inv.due_date && moment(inv.due_date).startOf("day").isBefore(today))
    .sort((a, b) => moment(a.due_date).diff(moment(b.due_date)));

  const dueSoonInvoices = unpaidInvoiceList
    .filter((inv) => {
      if (!inv.due_date) return false;
      const due = moment(inv.due_date).startOf("day");
      return due.isSameOrAfter(today) && due.isSameOrBefore(dueSoonEnd);
    })
    .sort((a, b) => moment(a.due_date).diff(moment(b.due_date)));

  const pendingDocList = allDocuments.filter((d) => d.status !== "done");

  const buildInvoiceActionRow = (inv, urgency) => {
    const dueBadge = getDueInBadge(inv);
    const amountPaid = Number(inv.amount_paid || 0);
    const subtitleParts = [];
    if (inv.due_date) subtitleParts.push(`Due ${formatInvoiceDate(inv.due_date)}`);
    if (dueBadge) subtitleParts.push(dueBadge.label);
    if (amountPaid > 0) subtitleParts.push(`${formatCurrency(amountPaid)} paid`);

    return {
      id: inv.id,
      type: "invoice",
      urgency,
      title: getInvoiceTitle(inv),
      subtitle: subtitleParts.join(" · "),
      amount: getInvoiceRemaining(inv),
      tab: INVOICES_TAB,
      cta: "View",
    };
  };

  const actionRows = [
    ...overdueInvoices.slice(0, MAX_OVERDUE_ROWS).map((inv) => buildInvoiceActionRow(inv, "overdue")),
    ...dueSoonInvoices.slice(0, MAX_DUE_SOON_ROWS).map((inv) => buildInvoiceActionRow(inv, "due_soon")),
  ];

  if (pendingDocList.length > 0) {
    const docTitles = pendingDocList
      .slice(0, 2)
      .map((d) => d.title)
      .filter(Boolean)
      .join(", ");
    actionRows.push({
      id: "pending-docs",
      type: "document",
      urgency: "document",
      title: `${pendingDocList.length} document${pendingDocList.length > 1 ? "s" : ""} still preparing`,
      subtitle: docTitles || "Your documents are being generated",
      tab: DOCUMENTS_TAB,
      cta: "View",
    });
  }

  const hasMoreOverdue = overdueInvoices.length > MAX_OVERDUE_ROWS;
  const hasMoreDueSoon = dueSoonInvoices.length > MAX_DUE_SOON_ROWS;
  const hasActionRequired = actionRows.length > 0;
  const hasOverdue = overdueInvoices.length > 0;

  const navigateToTab = (tab) => {
    if (typeof onNavigateTab === "function") onNavigateTab(tab);
  };

  const urgencyStyles = {
    overdue: { rowBg: "red.50", rowBorder: "red.200", icon: FiAlertCircle, iconColor: "red.500", amountColor: "red.700" },
    due_soon: { rowBg: "orange.50", rowBorder: "orange.200", icon: FiCalendar, iconColor: "orange.500", amountColor: COLORS.maastrichtBlue },
    document: { rowBg: "blue.50", rowBorder: "blue.200", icon: FiFileText, iconColor: "blue.500", amountColor: COLORS.maastrichtBlue },
  };

  // Prefer ongoing cert, else soonest upcoming, else first
  const featuredCert = [...certifications].sort((a, b) => {
    const aStatus = getCertStatus(a).status;
    const bStatus = getCertStatus(b).status;
    const rank = { ongoing: 0, upcoming: 1, completed: 2, unknown: 3 };
    return (rank[aStatus] ?? 3) - (rank[bStatus] ?? 3);
  })[0] || null;

  const featuredModules = featuredCert ? (modules[featuredCert.id] || []) : [];
  const sortedFeaturedModules = [...featuredModules].sort(
    (a, b) => moment(a.start_date).diff(moment(b.start_date))
  );
  const completedModules = sortedFeaturedModules.filter(
    (m) => getModuleStatus(m).label === "Completed"
  ).length;
  const progressPct =
    sortedFeaturedModules.length > 0
      ? Math.round((completedModules / sortedFeaturedModules.length) * 100)
      : 0;

  const [calendarMonth, setCalendarMonth] = useState(moment().startOf("month"));
  const [selectedDate, setSelectedDate] = useState(null);

  const handleDownloadSessionICS = (session) => {
    const dateStr = moment(session.date).format("YYYY-MM-DD");
    const sessionForICS = {
      date: dateStr,
      start_time: session.start_time,
      end_time: session.end_time,
      venue: session.venue,
      venueAddress: session.venueAddress,
      room: session.room,
      session_id: session.date,
      meeting_url: session.meeting_url || "",
      is_online: !!session.is_online || !!session.meeting_url,
    };
    downloadICS(
      generateICSContent(sessionForICS, session.moduleName),
      `${session.moduleName.replace(/\s+/g, "_")}_${moment(session.date).format("YYYYMMDD")}.ics`
    );
    if (!isMobile) {
      toast({
        title: "Calendar Event Downloaded",
        description: "Open the .ics file to add to your calendar.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCopyMeetingLink = async (url, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const ok = await copyMeetingLink(url);
    toast({
      title: ok ? "Link copied" : "Could not copy link",
      description: ok ? "Meeting link copied to clipboard." : "Please copy the link manually.",
      status: ok ? "success" : "error",
      duration: 2500,
      isClosable: true,
    });
  };

  const sessionActionButton = (session) => {
    const joinState = getJoinButtonState(session);
    if (joinState.meetingUrl) {
      return (
        <HStack spacing={1.5} onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
          {joinState.status === "ended" ? (
            <Badge
              colorScheme="gray"
              fontSize="2xs"
              textTransform="none"
              px={2.5}
              py={1}
              borderRadius="full"
              fontWeight="600"
            >
              Ended
            </Badge>
          ) : (
            <Button
              size="xs"
              colorScheme={joinState.status === "join" ? "green" : "blue"}
              variant="solid"
              borderRadius="full"
              leftIcon={joinState.status === "join" ? <Icon as={FiVideo} /> : undefined}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openMeetingLink(joinState.meetingUrl);
              }}
            >
              {joinState.label}
            </Button>
          )}
          <IconButton
            aria-label="Copy meeting link"
            title="Copy link"
            icon={<Icon as={FiCopy} />}
            size="xs"
            variant="outline"
            borderRadius="full"
            onClick={(e) => handleCopyMeetingLink(joinState.meetingUrl, e)}
          />
        </HStack>
      );
    }

    const d = moment(session.date).startOf("day");
    if (d.isBefore(today)) {
      return (
        <Badge
          colorScheme="gray"
          fontSize="2xs"
          textTransform="none"
          px={2.5}
          py={1}
          borderRadius="full"
          fontWeight="600"
        >
          Ended
        </Badge>
      );
    }
    return (
      <Badge
        colorScheme="blue"
        fontSize="2xs"
        textTransform="none"
        px={2.5}
        py={1}
        borderRadius="full"
        fontWeight="600"
        textAlign="center"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        w="100%"
      >
        Upcoming
      </Badge>
    );
  };

  const SectionTitle = ({ icon, color = "blue.500", children }) => (
    <HStack spacing={2} mb={{ base: 3, md: 4 }}>
      <Icon as={icon} color={color} boxSize={5} flexShrink={0} />
      <Heading
        fontSize={{ base: "sm", md: "md" }}
        fontWeight="700"
        color={COLORS.maastrichtBlue}
        letterSpacing="-0.01em"
        lineHeight="short"
      >
        {children}
      </Heading>
    </HStack>
  );

  // Match progress dots: completed=green, ongoing=blue, evaluation due=orange, upcoming=grey
  const getModuleBadgeColors = (label) => {
    if (label === EVALUATION_DUE_LABEL) return getEvaluationDueBadgeColors();
    if (label === "Completed") return { bg: "green.500", color: "white" };
    if (label === "Ongoing") return { bg: "blue.500", color: "white" };
    return { bg: "gray.200", color: "gray.700" };
  };

  const renderCalendar = () => {
    const startOfMonth = calendarMonth.clone().startOf("month");
    const endOfMonth = calendarMonth.clone().endOf("month");
    const startDay = startOfMonth.clone().startOf("week");
    const endDay = endOfMonth.clone().endOf("week");
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const weeks = [];
    let cursor = startDay.clone();
    while (cursor.isBefore(endDay)) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(cursor.clone());
        cursor.add(1, "day");
      }
      weeks.push(week);
    }

    const selectedDaySessions = selectedDate ? sessionsByDate[selectedDate] || [] : [];

    return (
      <Box>
        <Flex
          justify="space-between"
          align="center"
          mb={3}
          px={1}
          py={1}
          borderRadius="lg"
          bg="gray.50"
          border="1px solid"
          borderColor="gray.100"
        >
          <IconButton
            icon={<FiChevronLeft />}
            size="sm"
            variant="ghost"
            color="gray.600"
            aria-label="Previous month"
            onClick={() => setCalendarMonth((prev) => prev.clone().subtract(1, "month"))}
          />
          <Text fontWeight="700" fontSize={{ base: "sm", md: "md" }} color={COLORS.maastrichtBlue}>
            {calendarMonth.format("MMMM YYYY")}
          </Text>
          <IconButton
            icon={<FiChevronRight />}
            size="sm"
            variant="ghost"
            color="gray.600"
            aria-label="Next month"
            onClick={() => setCalendarMonth((prev) => prev.clone().add(1, "month"))}
          />
        </Flex>

        <Grid templateColumns="repeat(7, 1fr)" gap={1} mb={1}>
          {dayNames.map((d) => (
            <GridItem key={d} textAlign="center" py={1}>
              <Text fontSize={{ base: "2xs", md: "xs" }} fontWeight="600" color="gray.500">
                {d}
              </Text>
            </GridItem>
          ))}
        </Grid>

        {weeks.map((week, wi) => (
          <Grid key={wi} templateColumns="repeat(7, 1fr)" gap={1} mb={1}>
            {week.map((day, di) => {
              const dateKey = day.format("YYYY-MM-DD");
              const isCurrentMonth = day.month() === calendarMonth.month();
              const isToday = day.isSame(today, "day");
              const isSelected = selectedDate != null && dateKey === selectedDate;
              const daySessions = sessionsByDate[dateKey] || [];
              const hasSessions = daySessions.length > 0;
              const isPast = day.isBefore(today);

              return (
                <GridItem
                  key={di}
                  textAlign="center"
                  py={{ base: 1.5, md: 2 }}
                  px={0.5}
                  cursor={hasSessions ? "pointer" : "default"}
                  onClick={() => {
                    if (!hasSessions) return;
                    setSelectedDate((prev) => (prev === dateKey ? null : dateKey));
                  }}
                  borderRadius="lg"
                  bg={isSelected ? "blue.50" : "transparent"}
                  border="1px solid"
                  borderColor={isSelected ? "blue.300" : "transparent"}
                  _hover={hasSessions ? { bg: isSelected ? "blue.100" : "gray.50" } : {}}
                  transition="all 0.15s"
                >
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    w={{ base: "24px", md: "30px" }}
                    h={{ base: "24px", md: "30px" }}
                    mx="auto"
                    borderRadius="full"
                    bg={isToday && !isSelected ? "blue.500" : "transparent"}
                  >
                    <Text
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight={isToday || hasSessions ? "700" : "500"}
                      color={
                        !isCurrentMonth
                          ? "gray.300"
                          : isToday && !isSelected
                            ? "white"
                            : isSelected
                              ? "blue.700"
                              : hasSessions && !isPast
                                ? "gray.800"
                                : isPast
                                  ? "gray.400"
                                  : "gray.600"
                      }
                      lineHeight="1"
                    >
                      {day.date()}
                    </Text>
                  </Box>
                  {hasSessions && (
                    <HStack spacing={0.5} justify="center" mt={0.5} minH="6px">
                      {daySessions.slice(0, 3).map((_, si) => (
                        <Box
                          key={si}
                          w="4px"
                          h="4px"
                          borderRadius="full"
                          bg={isPast ? "gray.300" : isSelected ? "blue.500" : "blue.400"}
                        />
                      ))}
                    </HStack>
                  )}
                </GridItem>
              );
            })}
          </Grid>
        ))}

        {selectedDaySessions.length > 0 && (
          <Box mt={4} pt={4} borderTop="1px solid" borderColor="gray.100">
            <HStack spacing={2} mb={3} justify="space-between">
              <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="700" color={COLORS.maastrichtBlue}>
                {moment(selectedDate).format("dddd, D MMMM YYYY")}
              </Text>
              <Badge
                bg="blue.50"
                color="blue.700"
                fontSize="2xs"
                textTransform="none"
                borderRadius="full"
                px={2.5}
                py={0.5}
                fontWeight="600"
              >
                {selectedDaySessions.length} session{selectedDaySessions.length !== 1 ? "s" : ""}
              </Badge>
            </HStack>
            <VStack spacing={2.5} align="stretch">
              {selectedDaySessions.map((session, idx) => {
                const locationParts = [session.venue, session.room].filter(
                  (v) => v && v !== "TBC" && v !== "TBD"
                );
                const locationText = locationParts.join(" - ");
                const sessionDate = moment(session.date);
                return (
                  <Flex
                    key={`${session.moduleId}-${session.date}-${idx}`}
                    as={Link}
                    to={`/courses/${session.moduleId}`}
                    gap={3}
                    align="center"
                    px={{ base: 3, md: 3.5 }}
                    py={{ base: 3, md: 3.5 }}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor="gray.100"
                    bg="gray.50"
                    _hover={{ bg: "gray.100", borderColor: "gray.200" }}
                    transition="background 0.15s ease, border-color 0.15s ease"
                  >
                    <Box
                      bg="blue.500"
                      color="white"
                      borderRadius="lg"
                      px={{ base: 1.5, md: 2 }}
                      py={{ base: 1, md: 1.5 }}
                      textAlign="center"
                      minW={{ base: "36px", md: "48px" }}
                      flexShrink={0}
                    >
                      <Text fontSize="2xs" fontWeight="600" lineHeight="1">
                        {sessionDate.format("MMM").toUpperCase()}
                      </Text>
                      <Text fontSize={{ base: "md", md: "lg" }} fontWeight="800" lineHeight="1.1">
                        {sessionDate.format("DD")}
                      </Text>
                    </Box>
                    <Box flex="1" minW={0}>
                      <Text fontWeight="600" fontSize={{ base: "xs", md: "sm" }} color="gray.800" noOfLines={1}>
                        {session.moduleName}
                      </Text>
                      <HStack spacing={3} color="gray.500" fontSize="2xs" mt={0.5} flexWrap="wrap">
                        {session.start_time && session.end_time && (
                          <HStack spacing={1}>
                            <Icon as={FiClock} boxSize="11px" />
                            <Text>
                              {formatTime12h(session.start_time)} – {formatTime12h(session.end_time)}
                            </Text>
                          </HStack>
                        )}
                        {locationText && (
                          <HStack spacing={1} minW={0}>
                            <Icon as={FiMapPin} boxSize="11px" flexShrink={0} />
                            <Text noOfLines={1}>{locationText}</Text>
                          </HStack>
                        )}
                      </HStack>
                    </Box>
                    <VStack
                      spacing={1.5}
                      flexShrink={0}
                      align="stretch"
                      minW={{ base: "96px", md: "110px" }}
                      onClick={(e) => e.preventDefault()}
                    >
                      {sessionActionButton(session)}
                      <Button
                        size="xs"
                        colorScheme="blue"
                        variant="outline"
                        borderRadius="full"
                        leftIcon={<Icon as={FaCalendarPlus} boxSize={3} />}
                        onClick={() => handleDownloadSessionICS(session)}
                      >
                        Add to Cal
                      </Button>
                    </VStack>
                  </Flex>
                );
              })}
            </VStack>
          </Box>
        )}
      </Box>
    );
  };

  const stats = [
    { label: "Total Modules", value: totalCourses, color: "blue", icon: FiBook },
    { label: "This Week's Classes", value: upcomingSessions.length, color: "purple", icon: FiCalendar },
    { label: "Documents Pending", value: pendingDocs, color: "teal", icon: FiFileText },
    {
      label: "Unpaid Invoices",
      value: unpaidInvoices,
      color: unpaidInvoices > 0 ? "red" : "green",
      icon: FiDollarSign,
    },
  ];

  const quickActions = [
    { label: "Open My Courses", icon: FiBook, tab: 1, color: "blue" },
    { label: "View Documents", icon: FiFileText, tab: 2, color: "teal" },
    { label: "Pay Tuition Fee", icon: FiCreditCard, tab: 3, color: "orange" },
    { label: "View My Schedule", icon: FiCalendar, tab: 0, color: "purple", scrollTo: "schedule" },
    { label: "Talk to Admin", icon: FiMessageCircle, tab: 6, color: "gray" },
  ];

  const greetingPeriod =
    moment().hour() < 12 ? "Morning" : moment().hour() < 17 ? "Afternoon" : "Evening";

  const featuredIntake =
    featuredCert?.intake != null && String(featuredCert.intake).trim() !== ""
      ? String(featuredCert.intake).trim()
      : null;
  const featuredSlot = featuredCert
    ? getRunSlotLabel(featuredCert.id, featuredCert.run_name, featuredCert.code)
    : null;
  const featuredBatchLabel = [
    featuredIntake ? `Batch ${featuredIntake}` : null,
    featuredSlot,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <VStack spacing={{ base: 4, md: 5 }} align="stretch">
      {/* Pinned notices (show_on_top) — college + programme/intake */}
      {topNotices.length > 0 && (
        <VStack spacing={2} align="stretch">
          {topNotices.map((notice, index) => {
            const isCollege =
              !notice.course_type_code &&
              !notice.course_run_id &&
              !notice.full_cert_type_code &&
              !notice.full_cert_run_id;
            const style = getNoticeCardStyle(notice.notice_type, { college: isCollege });
            const bodyHtml = sanitizeNoticeHtml(notice.body);
            return (
              <Box
                key={notice.id}
                p={{ base: 3.5, md: 4 }}
                bg={style.bg}
                borderRadius="xl"
                border="1px solid"
                borderColor={style.border}
              >
                <HStack align="flex-start" spacing={3}>
                  <Icon as={FiBell} color={style.iconColor} boxSize={{ base: 4, md: 5 }} mt={0.5} flexShrink={0} />
                  <Box minW={0}>
                    <HStack spacing={2} mb={1} flexWrap="wrap">
                      <Text fontWeight="700" fontSize={{ base: "sm", md: "md" }} color={COLORS.maastrichtBlue}>
                        {notice.title}
                      </Text>
                      <Badge
                        bg={style.badgeBg}
                        color={style.badgeColor}
                        fontSize="2xs"
                        borderRadius="full"
                        px={2}
                        textTransform="none"
                      >
                        {style.label}
                      </Badge>
                    </HStack>
                    {bodyHtml ? (
                      <Box sx={noticeBodySx} dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                    ) : null}
                    <NoticeAttachments attachments={notice.attachments} compact />
                    {index === topNotices.length - 1 && (
                      <Button
                        mt={2}
                        size="xs"
                        variant="link"
                        color={COLORS.mandarin}
                        rightIcon={<FiArrowRight />}
                        onClick={() => typeof onNavigateTab === "function" && onNavigateTab(5)}
                      >
                        View all notices
                      </Button>
                    )}
                  </Box>
                </HStack>
              </Box>
            );
          })}
        </VStack>
      )}

      {/* Header + hero as one tight group */}
      <VStack spacing={{ base: 3, md: 4 }} align="stretch">
        <Flex align="center" justify="space-between" gap={3}>
          <Box minW={0}>
            <Heading
              as="h1"
              fontSize={{ base: "xl", md: "2xl" }}
              fontWeight="700"
              color={COLORS.maastrichtBlue}
              letterSpacing="-0.02em"
              lineHeight="1.15"
              mb={0}
            >
              Dashboard
            </Heading>
            <Text
              mt={1}
              fontSize={{ base: "xs", md: "sm" }}
              color="gray.500"
              fontWeight="500"
              lineHeight="short"
            >
              Your learning overview
            </Text>
          </Box>
          <HStack
            spacing={1.5}
            flexShrink={0}
            alignSelf="flex-start"
            mt={{ base: 0.5, md: 1 }}
            px={{ base: 2.5, md: 3 }}
            py={{ base: 1, md: 1.5 }}
            borderRadius="full"
            bg="gray.50"
            border="1px solid"
            borderColor="gray.200"
            color="gray.600"
          >
            <Icon as={FiCalendar} boxSize={{ base: 3, md: 3.5 }} />
            <Text fontSize={{ base: "2xs", md: "xs" }} fontWeight="600" whiteSpace="nowrap">
              {moment().format("ddd, D MMM")}
            </Text>
          </HStack>
        </Flex>

        {/* Welcome + featured course */}
        <Box
          borderRadius="2xl"
          bg={COLORS.maastrichtBlue}
          backgroundImage={`linear-gradient(135deg, ${COLORS.maastrichtBlue} 0%, #1a2749 55%, #243356 100%)`}
          color="white"
          p={{ base: 4, md: 6 }}
          overflow="hidden"
        >
          <Flex
            direction={{ base: "column", lg: "row" }}
            gap={{ base: 3, lg: 6 }}
            align={{ base: "stretch", lg: "center" }}
            justify="space-between"
          >
            <HStack align="flex-start" spacing={{ base: 2.5, md: 3 }} flex="1" minW={0}>
              <Icon as={FiSun} boxSize={{ base: 5, md: 7 }} color="yellow.300" mt={0.5} flexShrink={0} />
              <Box minW={0}>
                <Heading
                  fontSize={{ base: "md", md: "2xl" }}
                  fontWeight="700"
                  lineHeight="short"
                  mb={1}
                >
                  Good {greetingPeriod}, {greetingName}!
                </Heading>
                <Text fontSize={{ base: "2xs", md: "sm" }} color="whiteAlpha.800" lineHeight="tall">
                  {featuredCert
                    ? "Here's your program snapshot for today."
                    : "Enroll in a program to see your learning overview."}
                </Text>
              </Box>
            </HStack>

            {featuredCert && (
              <Box
                bg="whiteAlpha.150"
                border="1px solid"
                borderColor="whiteAlpha.300"
                borderRadius="xl"
                p={{ base: 3, md: 4 }}
                minW={{ lg: "280px" }}
                maxW={{ lg: "360px" }}
                flexShrink={0}
              >
                <Text fontSize="2xs" fontWeight="700" letterSpacing="0.06em" color="whiteAlpha.700" mb={1}>
                  YOUR COURSE
                </Text>
                <Text fontWeight="700" fontSize={{ base: "xs", md: "md" }} noOfLines={2} mb={featuredBatchLabel ? 1 : 2}>
                  {featuredCert.full_certification?.name || featuredCert.run_name}
                </Text>
                {featuredBatchLabel && (
                  <Text fontSize={{ base: "2xs", md: "xs" }} color="whiteAlpha.700" fontWeight="600" mb={1.5}>
                    {featuredBatchLabel}
                  </Text>
                )}
                <Text fontSize={{ base: "2xs", md: "xs" }} color="whiteAlpha.800">
                  {moment(featuredCert.start_date).format("DD MMM YYYY")} –{" "}
                  {moment(featuredCert.end_date).format("DD MMM YYYY")}
                </Text>
                <Badge mt={2} bg="whiteAlpha.200" color="white" fontSize="2xs" textTransform="none">
                  {getCertStatus(featuredCert).label}
                </Badge>
              </Box>
            )}
          </Flex>
        </Box>
      </VStack>

      {/* Stats */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 2.5, md: 4 }}>
        {stats.map((stat) => (
          <Box
            key={stat.label}
            p={{ base: 3, md: 4 }}
            borderRadius="xl"
            borderWidth="1px"
            borderColor="gray.200"
            borderLeftWidth="3px"
            borderLeftColor={`${stat.color}.400`}
            bg="white"
          >
            <HStack justify="space-between" mb={1} align="flex-start">
              <Text
                fontSize={{ base: "xl", md: "3xl" }}
                fontWeight="800"
                color={`${stat.color}.600`}
                lineHeight="1"
              >
                {stat.value}
              </Text>
              <Icon as={stat.icon} color={`${stat.color}.400`} boxSize={{ base: 4, md: 5 }} mt={0.5} />
            </HStack>
            <Text fontSize={{ base: "2xs", md: "sm" }} color="gray.600" lineHeight="short" fontWeight="500">
              {stat.label}
            </Text>
          </Box>
        ))}
      </SimpleGrid>

      {hasActionRequired && (
        <Box
          p={{ base: 3.5, md: 4 }}
          bg={hasOverdue ? "red.50" : "orange.50"}
          borderRadius="xl"
          border="1px solid"
          borderColor={hasOverdue ? "red.100" : "orange.100"}
        >
          <HStack spacing={2} mb={3}>
            <Icon as={FiAlertCircle} color={hasOverdue ? "red.500" : "orange.500"} boxSize={{ base: 4, md: 5 }} />
            <Text fontSize={{ base: "sm", md: "md" }} fontWeight="700" color={hasOverdue ? "red.700" : "orange.700"}>
              Action Required
            </Text>
          </HStack>

          <VStack spacing={2} align="stretch">
            {actionRows.map((row) => {
              const style = urgencyStyles[row.urgency] || urgencyStyles.due_soon;
              return (
                <Flex
                  key={row.id}
                  gap={3}
                  align={{ base: "flex-start", md: "center" }}
                  direction={{ base: "column", md: "row" }}
                  p={{ base: 3, md: 3 }}
                  bg={style.rowBg}
                  border="1px solid"
                  borderColor={style.rowBorder}
                  borderRadius="xl"
                >
                  <HStack spacing={3} flex="1" minW={0} align="center" w="100%">
                    <Icon as={style.icon} color={style.iconColor} boxSize={5} flexShrink={0} />
                    <Box minW={0} flex="1">
                      <Text fontWeight="700" fontSize={{ base: "xs", md: "sm" }} color="gray.800" noOfLines={2}>
                        {row.title}
                      </Text>
                      <Text fontSize="2xs" color="gray.600" mt={0.5} lineHeight="tall">
                        {row.subtitle}
                      </Text>
                    </Box>
                    {row.amount > 0 && (
                      <Text
                        fontWeight="800"
                        fontSize={{ base: "sm", md: "md" }}
                        color={style.amountColor}
                        flexShrink={0}
                        whiteSpace="nowrap"
                      >
                        {formatCurrency(row.amount)}
                      </Text>
                    )}
                  </HStack>
                  <Button
                    size="xs"
                    colorScheme={row.urgency === "overdue" ? "red" : row.urgency === "document" ? "blue" : "orange"}
                    variant={row.urgency === "overdue" ? "solid" : "outline"}
                    borderRadius="full"
                    rightIcon={<FiArrowRight />}
                    flexShrink={0}
                    alignSelf={{ base: "stretch", md: "center" }}
                    onClick={() => navigateToTab(row.tab)}
                  >
                    {row.cta}
                  </Button>
                </Flex>
              );
            })}
          </VStack>

          {(hasMoreOverdue || hasMoreDueSoon) && (
            <Button
              mt={3}
              size="xs"
              variant="link"
              color={hasOverdue ? "red.600" : "orange.600"}
              rightIcon={<FiArrowRight />}
              onClick={() => navigateToTab(INVOICES_TAB)}
            >
              {hasMoreOverdue && hasMoreDueSoon
                ? `View all ${overdueInvoices.length + dueSoonInvoices.length} invoices needing attention`
                : hasMoreOverdue
                  ? `View all ${overdueInvoices.length} overdue invoices`
                  : `View all ${dueSoonInvoices.length} invoices due within ${DUE_SOON_DAYS} days`}
            </Button>
          )}
        </Box>
      )}

      {/* Progress + this month's classes */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 3, md: 4 }}>
        <Box
          p={{ base: 4, md: 5 }}
          bg="white"
          borderRadius="xl"
          border="1px solid"
          borderColor="gray.200"
        >
          <SectionTitle icon={FiBook} color="blue.500">
            Course Progress
          </SectionTitle>
          {sortedFeaturedModules.length === 0 ? (
            <Text fontSize="sm" color="gray.500">
              No modules to show yet.
            </Text>
          ) : (
            <>
              <VStack align="stretch" spacing={2.5} mb={4}>
                {sortedFeaturedModules.map((mod) => {
                  const status = getModuleStatus(mod);
                  const needsEval = evaluationDueRunIds.has(mod.id);
                  const displayLabel = needsEval ? EVALUATION_DUE_LABEL : status.label;
                  const badgeColors = getModuleBadgeColors(displayLabel);
                  const isDone = displayLabel === "Completed";
                  const isOngoing = displayLabel === "Ongoing";
                  const isEvalDue = displayLabel === EVALUATION_DUE_LABEL;
                  const linkTo = isEvalDue
                    ? `/courses/${mod.id}?tab=evaluation`
                    : `/courses/${mod.id}`;
                  return (
                    <HStack
                      key={mod.id}
                      as={Link}
                      to={linkTo}
                      spacing={3}
                      p={2}
                      mx={-2}
                      borderRadius="lg"
                      _hover={{ bg: "gray.50", opacity: 1 }}
                    >
                      <Box
                        w="18px"
                        h="18px"
                        borderRadius="full"
                        border="2px solid"
                        borderColor={
                          isEvalDue
                            ? "orange.400"
                            : isDone
                              ? "green.400"
                              : isOngoing
                                ? "blue.500"
                                : "gray.300"
                        }
                        bg={
                          isEvalDue
                            ? "orange.400"
                            : isDone
                              ? "green.400"
                              : isOngoing
                                ? "blue.500"
                                : "transparent"
                        }
                        flexShrink={0}
                      />
                      <Box flex="1" minW={0}>
                        <Text
                          fontSize={{ base: "xs", md: "sm" }}
                          fontWeight={isOngoing || isEvalDue ? "700" : "500"}
                          color={isDone ? "gray.500" : "gray.800"}
                          textDecoration={isDone ? "line-through" : "none"}
                          noOfLines={1}
                        >
                          {mod.course_type?.name || mod.course_type_code || mod.id}
                        </Text>
                      </Box>
                      <Badge
                        bg={badgeColors.bg}
                        color={badgeColors.color}
                        fontSize="2xs"
                        textTransform="none"
                        flexShrink={0}
                        borderRadius="full"
                        px={2.5}
                        py={0.5}
                        fontWeight="600"
                      >
                        {displayLabel}
                      </Badge>
                    </HStack>
                  );
                })}
              </VStack>
              <Box>
                <HStack justify="space-between" mb={1.5}>
                  <Text fontSize="xs" color="gray.600" fontWeight="600">
                    {completedModules} of {sortedFeaturedModules.length} Modules Done
                  </Text>
                  <Text fontSize="xs" color="gray.500" fontWeight="600">
                    {progressPct}%
                  </Text>
                </HStack>
                <Progress value={progressPct} size="sm" colorScheme="blue" borderRadius="full" />
              </Box>
            </>
          )}
        </Box>

        <Box
          id="dashboard-schedule"
          p={{ base: 4, md: 5 }}
          bg="white"
          borderRadius="xl"
          border="1px solid"
          borderColor="gray.200"
        >
          <SectionTitle icon={FiCalendar} color="purple.500">
            This Month&apos;s Classes
          </SectionTitle>
          {(monthSessions.length > 0 ? monthSessions : upcomingSessions).length === 0 ? (
            <Text fontSize="sm" color="gray.500">
              No upcoming classes this month.
            </Text>
          ) : (
            <VStack align="stretch" spacing={2.5}>
              {(monthSessions.length > 0 ? monthSessions : upcomingSessions).slice(0, 5).map((session, idx) => {
                const sessionDate = moment(session.date);
                return (
                  <Flex
                    key={`${session.moduleId}-${session.date}-${idx}`}
                    as={Link}
                    to={`/courses/${session.moduleId}`}
                    gap={3}
                    align="center"
                    p={2.5}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor="gray.100"
                    bg="gray.50"
                    _hover={{ bg: "gray.100", borderColor: "gray.200" }}
                    transition="background 0.15s ease, border-color 0.15s ease"
                  >
                    <Box
                      bg="blue.500"
                      color="white"
                      borderRadius="lg"
                      px={{ base: 1.5, md: 2 }}
                      py={{ base: 1, md: 1.5 }}
                      textAlign="center"
                      minW={{ base: "36px", md: "48px" }}
                      flexShrink={0}
                    >
                      <Text fontSize="2xs" fontWeight="600" lineHeight="1">
                        {sessionDate.format("MMM").toUpperCase()}
                      </Text>
                      <Text fontSize={{ base: "md", md: "lg" }} fontWeight="800" lineHeight="1.1">
                        {sessionDate.format("DD")}
                      </Text>
                    </Box>
                    <Box flex="1" minW={0}>
                      <Text fontWeight="600" fontSize={{ base: "xs", md: "sm" }} noOfLines={{ base: 2, md: 1 }} color="gray.800">
                        {session.moduleName}
                      </Text>
                      <Text fontSize="2xs" color="gray.500" mt={0.5}>
                        {session.start_time && session.end_time
                          ? `${formatTime12h(session.start_time)} – ${formatTime12h(session.end_time)}`
                          : sessionDate.format("dddd")}
                      </Text>
                    </Box>
                    <Box
                      flexShrink={0}
                      onClick={(e) => e.preventDefault()}
                    >
                      {sessionActionButton(session)}
                    </Box>
                  </Flex>
                );
              })}
            </VStack>
          )}
        </Box>
      </SimpleGrid>

      {/* Quick actions */}
      <Box>
        <SectionTitle icon={FiHome} color="gray.500">
          Quick Actions
        </SectionTitle>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 5 }} spacing={3}>
          {quickActions.map((action) => (
            <Button
              key={action.label}
              variant="outline"
              borderColor={`${action.color}.200`}
              bg={`${action.color}.50`}
              color={`${action.color}.700`}
              leftIcon={<Icon as={action.icon} />}
              justifyContent="flex-start"
              h="auto"
              py={{ base: 2.5, md: 3 }}
              px={{ base: 3, md: 4 }}
              fontSize={{ base: "xs", md: "sm" }}
              fontWeight="600"
              whiteSpace="normal"
              textAlign="left"
              borderRadius="xl"
              _hover={{ bg: `${action.color}.100`, borderColor: `${action.color}.300` }}
              onClick={() => {
                if (typeof onNavigateTab === "function" && action.tab != null) {
                  onNavigateTab(action.tab);
                }
                if (action.scrollTo === "schedule") {
                  setTimeout(() => {
                    document.getElementById("dashboard-schedule")?.scrollIntoView({ behavior: "smooth" });
                  }, 50);
                }
              }}
            >
              {action.label}
            </Button>
          ))}
        </SimpleGrid>
      </Box>

      {/* Full calendar (kept for detail) */}
      {allSessions.length > 0 && (
        <Box p={{ base: 4, md: 5 }} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200">
          <SectionTitle icon={FiCalendar} color="blue.500">
            Session Calendar
          </SectionTitle>
          {!selectedDate && (
            <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500" mb={3} mt={-1.5}>
              Tap a date to see details
            </Text>
          )}
          {renderCalendar()}
        </Box>
      )}

      {allModules.length === 0 && upcomingSessions.length === 0 && !hasActionRequired && (
        <Box textAlign="center" py={6}>
          <Text color="gray.500" fontSize="md">
            No activity yet. Your dashboard will populate once you have active courses.
          </Text>
        </Box>
      )}
    </VStack>
  );
};
